import React, { useEffect, useState } from "react";
import { useController } from "@rest-hooks/react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

import EventResource from "resources/Profile/EventResource";

import CardGrid from "containers/public/Profile/Body/Events/EventsList/CardGrid";

import Compact from "ui/Modules/Event/Compact";
import Extended from "ui/Modules/Event/Extended";
import Button from "ui/Button";
import Description from "ui/text/Description";

import { useError } from "utils/useErrorController";

import { t } from "i18n/index";

import Skeleton from '@mui/material/Skeleton';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const Calendar = ({ view, hidePrice, hideLocation, organizationSlug }) => {
  
  const [events, setEvents] = useState([]);

  const {fetch} = useController();
  const {loading, setLoading, handleError} = useError();
  
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const events = await fetch(EventResource.list(), {organization_slug: organizationSlug});
      setEvents(events);
      setLoading(false);
    } catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    if (organizationSlug) {
      fetchEvents();
    }
  }, [organizationSlug]);

  const history = useHistory();
  const handleViewAll = () => {
    history.push(`/${organizationSlug}/events`);
  };

  const showViewAll = events.length > 0 && events[0].show_view_all;
  const onViewAll = showViewAll ? handleViewAll : null;

  const viewComponents = {
    empty: () => <div className="ui-calendar-empty"><span>No upcoming events</span></div>,
    list: () => <CompactView events={events} hidePrice={hidePrice} hideLocation={hideLocation} onViewAll={onViewAll} />,
    extended: () => <ExtendedView events={events} hidePrice={hidePrice} hideLocation={hideLocation} onViewAll={onViewAll} />,
    grid: () => <GridView events={events} onViewAll={onViewAll} />
  };

  if (loading) return <Loading />;

  return (
      <>{events.length > 0 ? (viewComponents[view] || viewComponents.extended)() : viewComponents.empty()}</>
  );
};

/* eslint-disable react/prop-types */
const CompactView = ({events, hidePrice, hideLocation, onViewAll}) => {

  return (
    <div className="ui-calendar-compact">
      {events.map((event, index) => (
        <Compact key={index} event={event} hidePrice={hidePrice} hideLocation={hideLocation} />
      ))}
      {onViewAll &&
        <div className='ui-event-compact view-all' onClick={onViewAll}>
          <div className='details'>
            <Description bold>{t('literal:viewAll')}</Description>
          </div>
          <Button highlighted small icon={ChevronRightIcon} onClick={onViewAll}></Button>
        </div>
      }
    </div>
  );
};
/* eslint-enable react/prop-types */

/* eslint-disable react/prop-types */
const ExtendedView = ({events, hidePrice, hideLocation, onViewAll}) => {

  return (
    <>
      <div className="ui-calendar-extended">
        {events.map((event, index) => (
          <Extended key={index} event={event} hidePrice={hidePrice} hideLocation={hideLocation} />
        ))}
      </div>
      {onViewAll &&
        <div className="view-all-button-container">
          <Button minwidth secondary icon={ChevronRightIcon} onClick={onViewAll}>{t('literal:viewAll')}</Button>
        </div>
      }
    </>
  );
};
/* eslint-enable react/prop-types */

/* eslint-disable react/prop-types */
const GridView = ({events, onViewAll}) => {
  return (
    <div className='grid-calendar'>
      <CardGrid events={events} />
      {onViewAll &&
        <div className="view-all-button-container">
          <Button minwidth secondary icon={ChevronRightIcon} onClick={onViewAll}>{t('literal:viewAll')}</Button>
        </div>
      }
    </div>
  );
};
/* eslint-enable react/prop-types */

const Loading = () => {
  return (
    <Skeleton variant="rounded" width="100%" height={180} sx={{ bgcolor: "var(--primaryBackgroundColor)" }} />
  );
};

Calendar.propTypes = {
  view: PropTypes.string,
  hidePrice: PropTypes.bool,
  hideLocation: PropTypes.bool,
  organizationSlug: PropTypes.string,
};

export default Calendar;
